import {
  IMinimalPairsTask,
  IMinimalPairsTaskItemSession,
  IMinimalPairsTaskSession,
  tMinimalPairsAnswer
} from 'common/modules/ReactShared';
import { t } from 'i18n';
import { useCallback, useEffect } from 'react';
import { usePromptContext } from 'students/views/shared/hooks';
import { customMediaQuery } from 'students/views/shared/styled';
import styled from 'styled-components';
import Streak from '../../common/components/Streak';
import { usePlayAnswers, useStreakFactor } from '../../common/hooks';
import PlayTaskScreenSwitcher from '../../common/screens/PlayTaskScreenSwitcher';
import { IPlayTaskProps, tPlayTaskComponentType } from '../types';
import { ISelectAnswerPayload } from './MinimalPairsItem';
import PlayMinimalPairsMainScreen from './PlayMinimalPairsMainScreen';
import bgImage from './assets/minimalPairsBg.svg';

const PlayMinimalPairsTask: tPlayTaskComponentType<
  IPlayTaskProps<IMinimalPairsTask, IMinimalPairsTaskSession>
> = ({ task, taskSession, isCompleting, onFinish, onNext, onAnswer, className }) => {
  const { items, instruction } = task;
  const answersFn = usePlayAnswers<tMinimalPairsAnswer>();

  const { streakNumber, streakPercent, onTheFirstTry } = useStreakFactor();
  const { makeDirty } = usePromptContext();

  useEffect(() => {
    if (taskSession?.taskItemSessions) {
      answersFn(taskSession.taskItemSessions);
    } else {
      answersFn(
        items.map((item, index) => {
          return {
            completed: false,
            id: index,
            taskItemId: item.id,
            mistakesCount: 0,
            skipped: false,
            url: ''
          };
        })
      );
    }

    onTheFirstTry(false);
  }, [taskSession, items, onTheFirstTry, answersFn]);

  const updateAnswer = (itemId: number, data: Partial<tMinimalPairsAnswer[number]>) => {
    const answerIndex = answersFn().findIndex(
      (answer: IMinimalPairsTaskItemSession) => answer.taskItemId === itemId
    );

    if (answerIndex < 0) return;

    const { mistakesCount, skipped } = data;

    const answer = answersFn()[answerIndex];
    const newAnswerData = { ...answer, ...data };

    answersFn([
      ...answersFn().slice(0, answerIndex),
      newAnswerData,
      ...answersFn().slice(answerIndex + 1)
    ]);

    onAnswer && onAnswer(newAnswerData);

    onTheFirstTry(!(mistakesCount || skipped));
  };

  const handleSelectAnswer = (payload: ISelectAnswerPayload) => {
    const { itemId, mistakesCount } = payload;

    updateAnswer(itemId, {
      mistakesCount,
      skipped: false
    });

    makeDirty(true);
  };

  const handleSkip = (itemId: number) => {
    updateAnswer(itemId, {
      skipped: true
    });
  };

  const handleFinishTask = () => {
    onNext && onNext();
  };

  const submitTask = useCallback(() => {
    onFinish && onFinish(answersFn());
  }, [onFinish, answersFn]);

  const handleCompleteTask = (goToFinishScreen: () => void) => () => {
    submitTask();

    goToFinishScreen();
  };

  return (
    <>
      <Streak streakNumber={streakNumber} streakPercent={streakPercent} />
      <PlayTaskScreenSwitcher
        isCompleting={isCompleting}
        onExitFinishScreen={handleFinishTask}
        className={className}
        startScreenHeading={
          instruction ||
          t('frontend.lesson_task.tasks.play.minimal_pairs.start_screen.heading')
        }
        itemsExist={!!items.length}
        taskStatus={taskSession?.status}
        completeTask={submitTask}
        taskColor="#f59989"
      >
        {(goToFinishScreen) => (
          <PlayMinimalPairsMainScreen
            task={task}
            onSelectAnswer={handleSelectAnswer}
            onComplete={handleCompleteTask(goToFinishScreen)}
            onSkip={handleSkip}
          />
        )}
      </PlayTaskScreenSwitcher>
    </>
  );
};

export default PlayMinimalPairsTask;

PlayMinimalPairsTask.backgroundComponent = styled.div`
  background-color: #f6647d;
  background-image: linear-gradient(180deg, #f6647d 0%, #f59989 100%);
  overflow: auto;

  ${customMediaQuery('tablet')} {
    background-image: url(${bgImage}), linear-gradient(180deg, #f6647d 0%, #f59989 100%);
    background-repeat: no-repeat, no-repeat;
    background-position: center;
    background-size: cover, auto;
  }
`;
PlayMinimalPairsTask.bgColor = '#f6647d';
